<template>
  <view-wrapper name="Counters" :loading="loading" loadingText="Loading counters">
    <router-link class="mt-3 mb-10 button big primary white" to="/counters/add">
      Create new counter
    </router-link>

    <expandabel-table
      :text="'Counters'"
      :headers="headers"
      :data="tableData"
      @searchQuery="searchText = $event"
    >
      <template v-slot:child="{ child }">
        <div class="flex flex-col p-4 rounded bg-primary-light">
          <div class="flex flex-wrap gap-6 mb-4">
            <div class="flex flex-col">
              <span class="text-xs font-bold">Description</span>
              <span class="text-gray-light">{{ child.description || '—' }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs font-bold">Start value</span>
              <span class="text-gray-light">{{ child.startValue }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs font-bold">Current value</span>
              <span class="text-gray-light">{{ child.currentValue }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs font-bold">Step value</span>
              <span class="text-gray-light">{{ child.stepValue }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs font-bold">Zero-pad length</span>
              <span class="text-gray-light">{{ child.zeroPadLength != null ? child.zeroPadLength : '—' }}</span>
            </div>
          </div>
          <div class="flex gap-4">
            <router-link
              :to="{ name: 'editcounter', params: { id: child.id } }"
              class="button small"
            >
              Edit <i style="font-size: 2em" class="edit-symbol" />
            </router-link>
            <button
              @click="openDeleteModal(child)"
              class="button small"
              type="button"
            >
              Delete
            </button>
          </div>
        </div>
      </template>
    </expandabel-table>

    <modal
      v-if="showDeleteModal"
      @close="closeDeleteModal"
      type="warning"
      heading="Delete counter"
    >
      <template slot="body">
        <p>
          This action <strong>cannot be undone</strong>. The counter will be permanently removed
          and will also be removed from all labels that use it. Any variables connected to this
          counter will no longer have a counter assigned.
        </p>
        <p class="mt-2">
          Type <strong>{{ counterToDelete && counterToDelete.name }}</strong> to confirm:
        </p>
        <grafokett-input
          class="mt-2"
          :editable="true"
          v-model="deleteConfirmName"
          labelPlaceholder="Counter name"
        />
      </template>
      <template slot="buttons">
        <grafokett-button
          :disabled="deleteConfirmName !== (counterToDelete && counterToDelete.name)"
          class="mt-4 primary"
          @click="confirmDelete"
        >
          Delete
        </grafokett-button>
      </template>
    </modal>

    <modal
      v-if="error"
      @close="error = false"
      type="error"
      heading="Error"
      :description="errorText"
    />
  </view-wrapper>
</template>

<script>
import ExpandabelTable from "../components/ExpandabelTable";
import Modal from "../components/Modal";
import GrafokettButton from "../components/forms/Button.vue";
import GrafokettInput from "../components/forms/Input.vue";

export default {
  components: {
    ExpandabelTable,
    Modal,
    GrafokettButton,
    GrafokettInput,
  },
  data() {
    return {
      headers: {
        name: "Name",
        currentValue: "Current value",
        stepValue: "Step",
      },
      loading: false,
      searchText: "",
      showDeleteModal: false,
      counterToDelete: null,
      deleteConfirmName: "",
      error: false,
      errorText: "",
    };
  },
  computed: {
    counters() {
      return this.$store.getters["COUNTERS/COUNTERS"];
    },
    tableData() {
      return this.counters
        .filter(c =>
          c.name.toLowerCase().includes(this.searchText.toLowerCase())
        )
        .map(c => ({
          id: c.id,
          name: c.name,
          currentValue: c.currentValue,
          stepValue: c.stepValue,
          expandableData: {
            id: c.id,
            name: c.name,
            description: c.description,
            startValue: c.startValue,
            currentValue: c.currentValue,
            stepValue: c.stepValue,
            zeroPadLength:
              c.zeroPaddedLength != null
                ? c.zeroPaddedLength
                : c.zeroPadLength,
          },
        }));
    },
  },
  methods: {
    openDeleteModal(counter) {
      this.counterToDelete = counter;
      this.deleteConfirmName = "";
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.counterToDelete = null;
      this.deleteConfirmName = "";
    },
    async confirmDelete() {
      try {
        await this.$store.dispatch("COUNTERS/DELETE_COUNTER", this.counterToDelete.id);
        this.closeDeleteModal();
        await this.$store.dispatch("COUNTERS/GET_COUNTERS");
      } catch (e) {
        this.closeDeleteModal();
        this.error = true;
        this.errorText =
          (e && e.response && e.response.data && e.response.data.Message) ||
          "Could not delete counter";
      }
    },
  },
  async mounted() {
    this.loading = true;
    await this.$store.dispatch("COUNTERS/GET_COUNTERS");
    this.loading = false;
  },
};
</script>
